import type { PanelEstudianteWire } from "@copi/shared";
import { FilePlus2, Loader2, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

import { SidebarHeader } from "./SidebarHeader";

import { StepperEtapas } from "@/features/dashboard/panel-estudiante/components/StepperEtapas";
import { usePanelEstudianteActivo } from "@/features/dashboard/panel-estudiante/use-panel-estudiante";

/**
 * Sidebar especializado para roles ESTUDIANTE/BACHILLER (ADR 016 — corrección
 * del Bloque D5).
 *
 * Reemplaza al `SidebarNavegacion` (administrativo) en el `AppShell` cuando
 * el rol del usuario es estudiante. Renderiza el `StepperEtapas` con las
 * 12 etapas del proceso de tesis del expediente activo del estudiante.
 *
 * Tres estados visuales:
 *  1. Cargando → spinner mínimo (durante el primer fetch del panel).
 *  2. Sin expediente → CTA "Crear primera solicitud" + texto educativo.
 *  3. Con expediente → stepper de 12 etapas con done/active/todo.
 *
 * Comparte el query `["panel-estudiante", "mio"]` con el `PanelEstudiante`
 * del outlet vía `usePanelEstudianteActivo`. TanStack Query deduplica
 * automáticamente — un solo fetch por render del shell.
 */
export function SidebarProcesoTesis() {
  const query = usePanelEstudianteActivo();

  return (
    <aside
      className="sidebar-surface hidden w-64 flex-col border-r border-border bg-card md:flex"
      aria-label="Proceso de tesis"
    >
      <SidebarHeader />

      <div className="flex-1 overflow-y-auto p-4">
        {query.isPending && (
          <div
            className="flex items-center gap-2 px-2 py-3 text-xs text-muted-foreground"
            role="status"
            aria-live="polite"
          >
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Cargando proceso…
          </div>
        )}

        {query.isError && (
          <p className="px-2 py-3 text-xs text-state-vencido" role="alert">
            No se pudo cargar el proceso. Revisá tu conexión.
          </p>
        )}

        {query.isSuccess && query.data === null && <EmptyEstudianteSidebar />}

        {query.isSuccess && query.data !== null && (
          <>
            <StepperEtapas etapaActual={query.data.expediente.etapaActual} />
            {/* ADR 017: si el expediente actual es CERRADO_REGULAR (titulado,
                rechazado, desaprobado), el estudiante puede iniciar otro
                proceso. Mostramos un CTA discreto debajo del stepper. */}
            {query.data.puedeCrearNuevo && <CtaNuevoProceso panel={query.data} />}
          </>
        )}
      </div>
    </aside>
  );
}

/**
 * Estado vacío del sidebar cuando el estudiante todavía no creó su
 * expediente. Solo texto educativo: el CTA "Iniciar solicitud" vive en el
 * panel principal (EstudianteDashboard) para no duplicar el botón en dos
 * superficies que se muestran juntas.
 */
function EmptyEstudianteSidebar() {
  return (
    <div className="rounded-lg border border-dashed border-border p-4 text-center">
      <p className="text-xs font-semibold text-foreground">Aún no iniciaste tu tesis</p>
      <p className="mt-1 text-[11px] text-muted-foreground">
        Una vez que inicies tu solicitud, verás aquí el avance por etapas.
      </p>
    </div>
  );
}

/**
 * CTA "Iniciar nuevo proceso" — solo visible cuando ADR 017 lo permite
 * (estado `CERRADO_REGULAR`). El mensaje se adapta al desenlace previo:
 * - `TITULO_OTORGADO`: "Iniciar segunda titulación".
 * - `SUSTENTACION_DESAPROBADA` / rechazos: "Iniciar nuevo intento".
 */
function CtaNuevoProceso({ panel }: { panel: PanelEstudianteWire }) {
  const etiqueta =
    panel.expediente.estadoActual === "TITULO_OTORGADO"
      ? "Iniciar nueva titulación"
      : "Iniciar nuevo intento";

  return (
    <div className="mt-4 rounded-lg border border-brand-500/30 bg-brand-50 p-3">
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-brand-700">
        <Sparkles className="h-3 w-3" aria-hidden="true" />
        Trámite cerrado
      </p>
      <p className="mt-1 text-[11px] text-foreground/80">
        Puedes iniciar otro proceso de tesis cuando quieras.
      </p>
      <Link
        to="/solicitudes/nueva"
        className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-2.5 py-1 text-[11px] font-semibold text-white transition hover:bg-brand-900"
      >
        <FilePlus2 className="h-3 w-3" aria-hidden="true" />
        {etiqueta}
      </Link>
    </div>
  );
}
